import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "featured" | "compact";
}

export default function ArticleCard({
  article,
  variant = "default",
}: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 py-3 border-b border-background-200 last:border-b-0 cursor-pointer"
      >
        <div className="w-20 h-16 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.views.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-4">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 mb-2">
              {article.categoryName}
            </span>
            <h3 className="text-lg md:text-xl font-heading font-bold text-white line-clamp-2 leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-white/70 mt-2 line-clamp-2 hidden md:block">
              {article.excerpt}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-accent-500 text-background-50">
            {article.categoryName}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-2">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.views.toLocaleString()} 阅读</span>
          </div>
          {article.isHot && (
            <span className="flex items-center gap-1 text-primary-600 font-medium">
              <i className="ri-fire-line text-xs"></i>
              热门
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}